import React, {useEffect, useState} from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchControlDepartments} from '@/entities/attendance/api';
import {dayTimesOf, describeWeek, weekRows} from '@/entities/attendance/schedule';
import {fetchCalendar} from '@/entities/calendar/api';
import {
  monthGrid,
  monthRange,
  monthTotals,
  rangeBetween,
  shiftMonth,
  toggleDates,
  weekdayDates,
  type MonthCell,
  type WeekTemplate,
} from '@/entities/calendar/month';
import {useCompanyPermissions} from '@/entities/permission/access';
import {useAuthStore} from '@/entities/session/auth-store';
import {DepartmentScheduleSheet} from '@/features/manage-department-schedule/ui/DepartmentScheduleSheet';
import {DaySheet} from '@/features/manage-schedule/ui/DaySheet';
import {MonthGrid, skinOf} from '@/features/manage-schedule/ui/MonthGrid';
import {SelectionBar} from '@/features/manage-schedule/ui/SelectionBar';
import {paint, type PaintMode} from '@/features/manage-schedule/lib/drag';
import {formatDayMinutes} from '@/shared/lib/format';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton, IconButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Page} from '@/shared/ui/page';
import {ChoiceChips} from '@/shared/ui/segmented';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatTile} from '@/shared/ui/stat-tile';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * «График работы» — отдельный раздел (2026-10-12): месяц календарём.
 *
 * До него график жил в двух местах настроек, и ни одно не отвечало на простой вопрос
 * «как отдел работает в этом месяце». Здесь это видно сразу: каждый день месяца с
 * часами, праздники и переносы цветом, норма месяца сверху. Нажатие на день — сделать
 * его праздником, выходным или рабочим (для одного дня или нескольких подряд);
 * «Изменить неделю» — недельный шаблон, из которого считаются обычные дни.
 *
 * Уровень — компания или отдел. У компании недельного шаблона нет (он у отделов),
 * поэтому её месяц — только праздники и переносы, которые наследуют все отделы.
 * Личные графики сотрудников перекрывают отдельский и правятся в карточке человека.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  bar: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), flexWrap: 'wrap'},
  month: {minWidth: 150, textAlign: 'center'},
  spacer: {flex: 1},
  tiles: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  tile: {flexBasis: 150, flexGrow: 1},
  week: {gap: spacing(2)},
  weekHead: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), flexWrap: 'wrap'},
  legend: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(3)},
  legendItem: {flexDirection: 'row', alignItems: 'center', gap: spacing(1)},
  swatch: {width: 14, height: 14, borderRadius: radii.sm, borderWidth: 1},
});

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const COMPANY = '';

export function ScheduleScreen() {
  const {t} = useTranslation();
  const p = useTheme();
  const context = useAuthStore(s => s.activeContext);
  const companyId = context?.companyId;
  const perms = useCompanyPermissions(context?.kind === 'company' ? companyId : undefined);
  // Владелец и личное пространство правят всегда; остальные — по праву на отделы.
  // Сервер проверяет каждый вызов сам, это только про то, показывать ли кнопки.
  const canEdit =
    context?.kind === 'home' ||
    (context?.kind === 'company' &&
      (context.role === 'owner' || perms.can('departments', 'update')));

  const today = todayIso();
  const [{year, month}, setYm] = useState(() => ({
    year: Number(today.slice(0, 4)),
    month: Number(today.slice(5, 7)),
  }));
  const [scope, setScope] = useState<string>(COMPANY);
  const [openDay, setOpenDay] = useState<MonthCell | null>(null);
  // Выделенные дни и последний отмеченный — опора для Shift+клик.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const clearSelection = () => {
    setSelected(new Set());
    setAnchor(null);
  };

  // Esc снимает выделение — в браузере это первое, что пробует рука.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelected(new Set());
        setAnchor(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const [editingWeek, setEditingWeek] = useState(false);

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId as string),
    enabled: !!companyId,
  });
  const dept = departments.data?.departments.find(d => d.department_id === scope) ?? null;

  const range = monthRange(year, month);
  const calendar = useQuery({
    queryKey: ['calendar', companyId, 'month', year, month, scope],
    queryFn: () =>
      fetchCalendar(companyId as string, {
        from: range.from,
        to: range.to,
        departmentId: scope || undefined,
      }),
    enabled: !!companyId,
  });

  const schedule = dept?.attendance_schedule ?? null;
  const template: WeekTemplate | null =
    scope && schedule
      ? {
          mask: schedule.workdays_mask,
          start: schedule.start_minutes ?? null,
          end: schedule.end_minutes ?? null,
          dayTimes: dayTimesOf(schedule),
        }
      : null;
  const exceptions = calendar.data ?? [];
  const cells = monthGrid(year, month, template, exceptions, scope);
  const totals = monthTotals(cells);

  const weekText = template
    ? describeWeek(
        weekRows(template.mask, template.start, template.end, [...template.dayTimes]),
        d => t(`home.person.weekday.${d}`),
        t('schedule.week.off'),
        m => formatDayMinutes(m) ?? '',
      )
    : null;

  const scopes = [
    {value: COMPANY, label: t('schedule.cal.company')},
    ...(departments.data?.departments ?? []).map(d => ({value: d.department_id, label: d.name})),
  ];

  // Другой месяц или уровень — другие дни: выделение оттуда здесь ничего не значит.
  const go = (step: number) => {
    clearSelection();
    setYm(cur => shiftMonth(cur.year, cur.month, step));
  };
  const changeScope = (next: string) => {
    clearSelection();
    setScope(next);
  };

  const pressDay = (cell: MonthCell, shift: boolean) => {
    if (shift && anchor) {
      setSelected(cur => new Set([...cur, ...rangeBetween(cells, anchor, cell.date)]));
    } else {
      setSelected(cur => toggleDates(cur, [cell.date]));
    }
    setAnchor(cell.date);
  };
  const paintDays = (dates: string[], mode: PaintMode) => {
    setSelected(cur => paint(cur, dates, mode));
    setAnchor(dates[dates.length - 1] ?? null);
  };
  const pressWeekday = (weekday: number) =>
    setSelected(cur => toggleDates(cur, weekdayDates(cells, weekday)));
  const selectedDates = [...selected].sort();
  const single = selectedDates.length === 1 ? cells.find(c => c.date === selectedDates[0]) : null;
  const thisMonth = year === Number(today.slice(0, 4)) && month === Number(today.slice(5, 7));
  const hoursText = `${Math.floor(totals.minutes / 60)}${
    totals.minutes % 60 ? `:${String(totals.minutes % 60).padStart(2, '0')}` : ''
  }`;

  const legend: Array<{kind: Parameters<typeof skinOf>[0]; label: string}> = [
    {kind: 'work', label: t('schedule.cal.kind.work')},
    {kind: 'off', label: t('schedule.cal.kind.off')},
    {kind: 'holiday', label: t('calendar.kind.holiday')},
    {kind: 'day_off', label: t('calendar.kind.day_off')},
    {kind: 'workday', label: t('calendar.kind.workday')},
  ];

  return (
    <Page
      scroll
      width="wide"
      title={t('schedule.cal.title')}
      subtitle={t('schedule.cal.subtitle')}
      refreshing={calendar.isRefetching || departments.isRefetching}
      onRefresh={() => {
        void calendar.refetch();
        void departments.refetch();
      }}
      testID="schedule-screen">
      <View style={styles.root}>
        <ChoiceChips options={scopes} value={scope} onChange={changeScope} testID="schedule-scope" />

        <View style={styles.bar}>
          <IconButton icon="chevronLeft" label={t('schedule.cal.prev')} onPress={() => go(-1)} />
          <AppText variant="subtitle" style={styles.month}>
            {`${t(`calendar.month.${month}`)} ${year}`}
          </AppText>
          <IconButton icon="chevron" label={t('schedule.cal.next')} onPress={() => go(1)} />
          {!thisMonth ? (
            <AppButton
              title={t('schedule.cal.today')}
              tone="ghost"
              size="sm"
              onPress={() => {
                clearSelection();
                setYm({year: Number(today.slice(0, 4)), month: Number(today.slice(5, 7))});
              }}
            />
          ) : null}
        </View>

        <View style={styles.tiles}>
          {template ? (
            <>
              <StatTile
                style={styles.tile}
                compact
                label={t('schedule.cal.workDays')}
                value={totals.workDays}
                tone="info"
              />
              <StatTile
                style={styles.tile}
                compact
                label={t('schedule.cal.hours')}
                value={template.start != null ? hoursText : '—'}
              />
            </>
          ) : null}
          <StatTile
            style={styles.tile}
            compact
            label={t('schedule.cal.holidays')}
            value={totals.holidays}
          />
          {template ? (
            <StatTile
              style={styles.tile}
              compact
              label={t('schedule.cal.daysOff')}
              value={totals.daysOff}
            />
          ) : null}
        </View>

        {scope ? (
          <Card padding={4} style={styles.week}>
            <View style={styles.weekHead}>
              <AppText variant="label" style={styles.spacer}>
                {t('schedule.cal.week')}
              </AppText>
              {canEdit && dept ? (
                <AppButton
                  title={t('schedule.cal.editWeek')}
                  tone="soft"
                  size="sm"
                  icon="clock"
                  onPress={() => setEditingWeek(true)}
                  testID="schedule-edit-week"
                />
              ) : null}
            </View>
            <AppText variant="body">{weekText ?? t('schedule.cal.noWeek')}</AppText>
            <AppText variant="caption">{t('department.schedulePersonalNote')}</AppText>
          </Card>
        ) : (
          <AppText variant="caption">{t('schedule.cal.companyHint')}</AppText>
        )}

        {calendar.isError ? (
          <Banner tone="error" title={messageOf(calendar.error, t('errors.unknown'))} />
        ) : null}

        {canEdit && companyId ? (
          <SelectionBar
            companyId={companyId}
            departmentId={scope || undefined}
            dates={selectedDates}
            exceptions={exceptions}
            onDone={clearSelection}
            onClear={clearSelection}
            onDetails={single ? () => setOpenDay(single) : undefined}
          />
        ) : null}

        {calendar.isPending ? (
          <Skeleton height={420} radius={radii.lg} />
        ) : (
          <MonthGrid
            cells={cells}
            today={today}
            selected={selected}
            onPress={canEdit ? pressDay : undefined}
            onPaint={canEdit ? paintDays : undefined}
            onWeekdayPress={canEdit ? pressWeekday : undefined}
          />
        )}

        <View style={styles.legend}>
          {legend.map(l => {
            const skin = skinOf(l.kind, p);
            return (
              <View key={l.kind} style={styles.legendItem}>
                <View style={[styles.swatch, {backgroundColor: skin.bg, borderColor: skin.border}]} />
                <AppText variant="caption">{l.label}</AppText>
              </View>
            );
          })}
          <View style={styles.legendItem}>
            <AppText variant="caption">● {t('schedule.cal.inheritedLegend')}</AppText>
          </View>
        </View>
        <AppText variant="caption">
          {t(canEdit ? 'schedule.cal.tapHint' : 'schedule.cal.readOnly')}
        </AppText>
      </View>

      {companyId && openDay ? (
        <DaySheet
          key={`${scope}:${openDay.date}`}
          companyId={companyId}
          departmentId={scope || undefined}
          cell={openDay}
          exceptions={exceptions}
          visible
          onClose={() => {
            setOpenDay(null);
            clearSelection();
          }}
        />
      ) : null}

      {companyId && dept && editingWeek ? (
        <DepartmentScheduleSheet
          key={`week:${dept.department_id}`}
          companyId={companyId}
          departmentId={dept.department_id}
          schedule={schedule}
          visible
          onClose={() => setEditingWeek(false)}
        />
      ) : null}
    </Page>
  );
}
